import React from 'react';
import { Alert, Button, Input, Select, Tag, Typography } from 'antd';
import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import {
  CandidateSummaryGap,
  CandidateSummaryGapStatus,
  CandidateSummaryPayload,
} from '../models/candidate-summary.model';
import {
  ASSESSMENT_SECTIONS,
  gapStatusColor,
  gapStatusLabel,
} from '../utils/candidate-summary-labels';
import {
  countOpenGaps,
  createEmptyGap,
  normalizeAssessmentGaps,
} from '../utils/candidate-summary-gaps';

const { TextArea } = Input;
const { Text } = Typography;

const labelClass = 'text-left block mb-2 text-sm font-medium text-gray-600';

interface CandidateSummaryGapsSectionProps {
  payload: CandidateSummaryPayload;
  editing?: boolean;
  onChange?: (gaps: CandidateSummaryGap[]) => void;
}

function updateGap(
  gaps: CandidateSummaryGap[],
  gapId: string,
  patch: Partial<CandidateSummaryGap>
): CandidateSummaryGap[] {
  return gaps.map((gap) => (gap.id === gapId ? { ...gap, ...patch } : gap));
}

export const CandidateSummaryGapsSection: React.FC<CandidateSummaryGapsSectionProps> = ({
  payload,
  editing = false,
  onChange,
}) => {
  const gaps = normalizeAssessmentGaps(payload);
  const openGapCount = countOpenGaps(payload);

  const emitChange = (nextGaps: CandidateSummaryGap[]) => {
    onChange?.(nextGaps);
  };

  return (
    <div className="text-left space-y-3">
      {openGapCount > 0 && (
        <Alert
          type="warning"
          showIcon
          message={`${openGapCount} open gap${openGapCount === 1 ? '' : 's'} need recruiter review`}
          description={ASSESSMENT_SECTIONS.gapsOpenAlert}
          className="text-left"
        />
      )}

      <div>
        <h3 className="text-base sm:text-lg font-semibold mb-2 text-gray-800 text-left">
          {ASSESSMENT_SECTIONS.gapsReviewTitle}
        </h3>
        <p className="text-sm text-gray-500 mb-3 leading-relaxed text-left">
          {ASSESSMENT_SECTIONS.gapsReviewIntro}
        </p>
      </div>

      {gaps.length === 0 && !editing && (
        <p className="text-sm text-gray-500 italic m-0">No gaps or risks flagged.</p>
      )}

      <div className="space-y-3">
        {gaps.map((gap) => (
          <article
            key={gap.id}
            className={`rounded-lg border p-3 sm:p-4 text-left ${
              gap.status === 'OPEN'
                ? 'border-amber-300 bg-amber-50/50'
                : 'border-green-200 bg-green-50/40'
            }`}
          >
            {editing ? (
              <div className="space-y-3">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                  <span className={labelClass}>Status</span>
                  <Select
                    className="w-full sm:w-56 rounded-lg"
                    value={gap.status}
                    options={(
                      Object.keys(gapStatusLabel) as CandidateSummaryGapStatus[]
                    ).map((status) => ({
                      value: status,
                      label: gapStatusLabel[status],
                    }))}
                    onChange={(status: CandidateSummaryGapStatus) =>
                      emitChange(
                        updateGap(gaps, gap.id, {
                          status,
                          resolutionNote:
                            status === 'OPEN' ? undefined : gap.resolutionNote,
                        })
                      )
                    }
                  />
                </div>
                <div>
                  <span className={labelClass}>Gap description</span>
                  <TextArea
                    className="rounded-lg"
                    rows={3}
                    value={gap.description}
                    onChange={(event) =>
                      emitChange(
                        updateGap(gaps, gap.id, { description: event.target.value })
                      )
                    }
                  />
                </div>
                {gap.status === 'RESOLVED' && (
                  <div>
                    <span className={labelClass}>{ASSESSMENT_SECTIONS.gapResolutionNote}</span>
                    <TextArea
                      className="rounded-lg"
                      rows={3}
                      value={gap.resolutionNote ?? ''}
                      placeholder="Explain how this was validated or addressed before sharing with the client."
                      onChange={(event) =>
                        emitChange(
                          updateGap(gaps, gap.id, {
                            resolutionNote: event.target.value,
                          })
                        )
                      }
                    />
                  </div>
                )}
                <Button
                  type="text"
                  danger
                  icon={<MinusCircleOutlined />}
                  className="!px-0"
                  onClick={() => emitChange(gaps.filter((item) => item.id !== gap.id))}
                >
                  Remove gap
                </Button>
              </div>
            ) : (
              <div className="space-y-2">
                <Tag color={gapStatusColor[gap.status]} className="rounded-md m-0">
                  {gap.status === 'OPEN'
                    ? ASSESSMENT_SECTIONS.gapStatusOpen
                    : ASSESSMENT_SECTIONS.gapStatusResolved}
                </Tag>
                <Text className="block text-sm sm:text-base text-gray-800 leading-relaxed break-words">
                  {gap.description}
                </Text>
                {gap.status === 'RESOLVED' && gap.resolutionNote && (
                  <Text type="secondary" className="block text-sm leading-relaxed break-words">
                    {ASSESSMENT_SECTIONS.gapResolutionNote}: {gap.resolutionNote}
                  </Text>
                )}
              </div>
            )}
          </article>
        ))}
      </div>

      {editing && (
        <Button
          type="dashed"
          icon={<PlusOutlined />}
          className="w-full sm:w-auto"
          onClick={() => emitChange([...gaps, createEmptyGap()])}
        >
          Add gap
        </Button>
      )}
    </div>
  );
};
